import axios from "axios";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  Loader2,
  Calendar,
  Briefcase,
  Building2,
  Video as VideoIcon,
  Sparkles,
} from "lucide-react";
import { API } from "@/context/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const WEEKDAY_LONG = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
];

function formatStartsAt(iso) {
  try {
    const d = new Date(iso);
    return `${WEEKDAY_LONG[d.getDay()]}, ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} às ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch {
    return iso;
  }
}

const PublicInterviewConfirmPage = () => {
  const { interviewId } = useParams();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code") || "";

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useDocumentTitle(
    info?.job_title
      ? `Entrevista — ${info.job_title}`
      : "Confirmar entrevista",
    { exact: true },
  );

  useEffect(() => {
    if (!interviewId || !code) {
      setError("Link inválido ou expirado.");
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/video/public/interviews/${interviewId}`, {
        params: { code },
      })
      .then((res) => {
        setInfo(res.data);
        setConfirmed(!!res.data.confirmed_by_candidate);
      })
      .catch((err) =>
        setError(err?.response?.data?.detail || "Não foi possível abrir esta entrevista."),
      )
      .finally(() => setLoading(false));
  }, [interviewId, code]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await axios.post(
        `${API}/video/public/interviews/${interviewId}/confirm`,
        null,
        { params: { code } },
      );
      setConfirmed(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Falha ao confirmar.");
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 to-pink-50">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 max-w-md w-full p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-rose-100 mx-auto flex items-center justify-center mb-4">
            <span className="text-rose-600 text-2xl">!</span>
          </div>
          <h1 className="text-lg font-bold text-slate-900 mb-1">
            Não foi possível abrir
          </h1>
          <p className="text-sm text-slate-600">
            {error || "Link inválido ou expirado."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-pink-50 px-4 py-10 flex items-start justify-center">
      <div
        data-testid="confirm-card"
        className="bg-white rounded-2xl shadow-xl border border-slate-200 max-w-lg w-full overflow-hidden"
      >
        <div className="bg-gradient-to-br from-violet-600 via-fuchsia-600 to-pink-600 text-white px-6 py-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold opacity-90 mb-1">
            <Sparkles className="w-3.5 h-3.5" />
            GridRecruiter
          </div>
          <h1 className="text-xl font-bold leading-tight">
            Sua entrevista está marcada
          </h1>
        </div>

        <div className="p-6 space-y-5">
          <p className="text-sm text-slate-700">
            Olá <strong>{info.candidate_name || "candidato(a)"}</strong>! 👋
          </p>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
            <div className="flex items-start gap-3 text-sm">
              <Calendar className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" />
              <span className="text-slate-800">
                {formatStartsAt(info.starts_at)}
                {info.duration_minutes
                  ? ` (~${info.duration_minutes} min)`
                  : ""}
              </span>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <Briefcase className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" />
              <span className="text-slate-800">{info.job_title}</span>
            </div>
            {info.organization_name && (
              <div className="flex items-start gap-3 text-sm">
                <Building2 className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" />
                <span className="text-slate-800">{info.organization_name}</span>
              </div>
            )}
          </div>

          {info.room_url && (
            <a
              href={info.room_url}
              className="flex items-center justify-center gap-1.5 w-full bg-violet-600 hover:bg-violet-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors text-sm shadow-sm"
              data-testid="open-room"
            >
              <VideoIcon className="w-4 h-4 shrink-0" />
              <span>Entrar na sala de vídeo</span>
            </a>
          )}

          {confirmed ? (
            <div
              data-testid="confirmed-banner"
              className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800"
            >
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              <span>
                Presença <strong>confirmada</strong>! O entrevistador já foi avisado.
              </span>
            </div>
          ) : (
            <button
              type="button"
              data-testid="confirm-button"
              onClick={handleConfirm}
              disabled={confirming}
              className="flex items-center justify-center gap-1.5 w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white font-semibold py-3 px-4 rounded-lg transition-colors text-sm shadow-sm"
            >
              {confirming ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Confirmando...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Confirmar minha presença
                </>
              )}
            </button>
          )}

          <p className="text-[11px] text-slate-500 text-center leading-snug">
            Precisa reagendar ou tirar dúvidas? Responda ao e-mail ou WhatsApp
            que recebeu sobre esta entrevista.
          </p>
        </div>

        <div className="bg-slate-50 px-6 py-3 border-t border-slate-200 text-center">
          <span className="text-[10px] text-slate-400">
            Enviado pelo <strong>GridRecruiter</strong>
          </span>
        </div>
      </div>
    </div>
  );
};

export default PublicInterviewConfirmPage;
